import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';

import EventEmitter from 'events';

/**
 * Controllers
 */

import AnimationController from './controllers/animation';
import DetectController from './controllers/detect';
import HeightController from './controllers/height';
import PageController from './controllers/page';
import ResizeController from './controllers/resize';
import RoutingController from './controllers/routing';
import ScrollController from './controllers/scroll';
import SkrolliteController from './controllers/skrollite';
import SectionController from './controllers/section';
import TransitionController from './controllers/transition';

/**
 * Components
 */

import Canvas from './components/canvas';
import Cursor from './components/cursor';
import Preloader from './components/preloader';
import Projects from './components/projects';

// use to find sections
// import Header from '../../modules/header/script';

gsap.registerPlugin(SplitText);

class App extends EventEmitter {

	el = {};

	splitHeadings = [];

	isDesktop = false;
	isIframe = false;
	isInitialized = false;
	isPreload = true;

	canvas
	cursor;
	preloader;
	projects;

	slug;

	mouse = {
		x: window.innerWidth / 2,
		y: window.innerHeight / 2
	};

	constructor() {
		super();

		this.isDesktop = DetectController.isDesktop;
		this.isDesktopXL = DetectController.isDesktopXL;

		this.addEventListeners();
	}

	checkScroll() {
		if (!this.isDesktop) {
			ScrollController.rendering = false;
			ScrollController.ease.duration = 20;
			ScrollController.removeStyles();
		}
	}

	gatherElements() {
		this.el = {
			menu: document.querySelector('.menu'),
			header: document.querySelector('.header'),
			headerLogo: document.querySelector('.header__logo'),
			headerItems: document.querySelectorAll('.header__item'),
			footer: document.querySelector('.footer'),
			main: document.querySelector('main.smooth-scroll'),
			content: document.querySelector('.smooth-scroll__content'),
			overlay: document.querySelector('.overlay'),
			projects: document.querySelector('.projects'),
			cursor: document.querySelector('.cursor'),
			preloader: document.querySelector('.preloader')
		};
	}

	/**
	 * Preloader Component
	 */

	createPreloader() {
		this.preloader = new Preloader({
			slug: this.slug,
			container: this.el.preloader
		});

		this.freeze();

		this.preloader.on('loadtexture', this.#onPreloadTexture);
		this.preloader.on('complete', this.#onPreloadComplete);
		this.preloader.show();
	}

	/**
	 * Preloader Component Events
	 */

	#onPreloadTexture = (data) => {
		if (this.canvas) this.canvas.loadModules(data);
	}

	#onPreloadComplete = () => {
		if (this.isDesktop) {
			this.isPreload = false;

			if (this.canvas) {
				this.canvas.complete();
			}

			ScrollController.refresh(true);
			TransitionController.show(this.page, !this.isPreload);

			if (this.slug !== 'home') {
				this.thaw();
			}

			if (this.cursor) {
				this.cursor.complete();
			}

			this.emit('preloadComplete');
		} else {
			ScrollController.refresh(true);
			this.thaw();
		}
	}

	/**
	 * Canvas Component
	 */

	createCanvas() {
		this.canvas = new Canvas({
			slug: this.slug
		});
	}

	/**
	* Cursor Component
	*/

	createCursor() {
		this.cursor = new Cursor(this.el.cursor);
	}

	/**
	 * Projects Component
	 */

	createProjects() {
		this.projects = new Projects({
			container: this.el.projects
		});
	}

	/**
	 * Components
	 */

	createComponents() {
		if (this.isDesktop) {
			this.createCanvas();
		}

		if (this.isDesktopXL) {
			this.createCursor();
		}

		this.createPreloader();
		this.createProjects();
	}

	/**
	 * Controllers
	 */

	configureControllers() {
		ResizeController.initialize();
		HeightController.initialize();
		PageController.initialize(this.el.content);

		TransitionController.initialize({ wrap: this.el.header, logo: this.el.headerLogo, items: this.el.headerItems }, this.el.footer, this.el.main);

		const { pending, done, failed } = SectionController.status;
		SectionController.monit = [pending, done, failed];
		SectionController.initialize(this.el.content);
		SectionController.seekInitialize();

		ScrollController.initialize(this.el.main, [
			'[data-uri] > section',
			'.footer'
		]);

		AnimationController.start();

		SkrolliteController.initialize();

		RoutingController.start({
			'/': async () => {
				await this.open('home');
			},
			'/:page': async (page) => {
				await this.open(page);
			},
			'/:channel/:page': async (channel, page) => {
				await this.open(`${channel}/${page}`);
			},
			'/projects': async () => {
				await this.open('home');
				if (this.projects) {
					this.projects.show();
				}
			}
		});
	}

	/**
	 * App Methods
	 */

	activate(uri) {
		const page = uri.split('/')[0];
		this.activatedPage = page;
	}

	freeze() {
		ScrollController.lock();
		if (this.isDesktop) ScrollController.rendering = false;
	}

	thaw() {
		ScrollController.unlock();
		if (this.isDesktop) ScrollController.rendering = true;
	}

	dim() {
		this.el.overlay.classList.add('is-shown');
	}

	undim() {
		this.el.overlay.classList.remove('is-shown');
	}

	async open(uri) {
		if (window.ga) window.ga('send', 'pageview');

		let wasActive = null;

		if (!this.isInitialized) {
			this.isInitialized = true;
			this.page = PageController.detect();
			this.slug = uri;
			this.show(this.slug, this.page);
		} else {
			wasActive = PageController.active;

			if (uri === wasActive) return;

			this.freeze();

			document.body.style.cursor = 'progress';

			if (this.isDesktop) {
				const currentAnchor = RoutingController.getCurrentAnchor();
				const isTop = window.scrollY < window.innerHeight;
				const isNextCaseStudy = currentAnchor?.hasAttribute('data-next-case-study') && uri.includes('case-studies/');
				const isNextNewsDetail = currentAnchor?.hasAttribute('data-next-news-detail') && uri.includes('news/');
				const isNextTransition = isNextCaseStudy || isNextNewsDetail;

				if (isNextTransition) {
					this.canvas.currentBackground.changeCaseStudy();
				}

				let hideTransition;

				if (isNextCaseStudy) {
					hideTransition = { type: 'case-study' };
				} else if (isNextNewsDetail) {
					hideTransition = { type: 'news-detail', currentAnchor };
				} else {
					hideTransition = { type: 'default' };
				}

				Promise.all([
					this.canvas.hide(isTop),
					TransitionController.hide(this.page, hideTransition)
				]).then(async () => {
					RoutingController.setProgress(wasActive, ScrollController.eased);

					this.page = await PageController.open(uri);

					if (this.cursor) this.cursor.reset();

					this.close(uri, wasActive);

					if (!this.isPreload) {
						if (this.canvas) this.canvas.show(this.slug, this.isPreload);
						TransitionController.show(this.page, this.isPreload, isNextTransition);
					}

					this.show(this.slug, this.page, isNextTransition);
					isNextTransition ? this.emit('pageTransitionNext') : this.emit('pageTransition');
				});
			} else {
				RoutingController.setProgress(wasActive, ScrollController.eased);

				this.page = await PageController.open(uri);

				this.close(uri, wasActive);
				this.show(this.slug, this.page);
			}
		}
	}

	close(uri, wasActive) {
		SectionController.destroyPage(wasActive);
		PageController.close(wasActive);
		RoutingController.restoreProgress(uri);
		this.slug = uri;
	}

	show(slug, page, isNextTransition = false) {
		this.activate(slug);
		this.apply(page, slug);
		this.refresh(true);
		this.split();

		if (isNextTransition) {
			setTimeout(() => this.thaw(), 1000);
		} else {
			this.thaw();
		}

		document.body.style.cursor = '';
	}

	apply(el, uri) {
		RoutingController.overrideAnchors();
		SectionController.seekInitialize(el, uri);
	}

	destroy(el) {
		SectionController.seekDestroy(el);
		this.refresh(true);
	}

	refresh(gather) {
		ScrollController.refresh(gather);

		if (this.isDesktop) {
			setTimeout(() => {
				SkrolliteController.refresh();
			}, 100);

			if (this.cursor) {
				this.cursor.refresh();
			}
		}
	}

	/**
	 * Split Headings
	 */

	split() {
		this.splitHeadings = [];
		const splitHeadings = document.querySelectorAll('[data-split-heading]');
		for (const heading of splitHeadings) {
			const split = new SplitText(heading, { type: 'lines' });
			this.splitHeadings.push(new SplitText(split.lines, { type: 'lines' }));
		}
	}

	/**
	 * Animation
	 */

	start() {
		this.animation = AnimationController.set({
			update: this.#update,
			render: this.#render
		});
	}

	#update = () => true;

	#render = () => {
		if (this.canvas) {
			this.canvas.update(ScrollController.eased);
		}

		if (this.cursor) {
			this.cursor.update();
		}
	}

	/**
	 * Global Event Listeners
	 */

	#onWindowResize = () => {
		this.isDesktop = DetectController.isDesktop;

		if (this.isDesktop) {
			if (this.canvas) this.canvas.resize(ScrollController.eased);
			if (this.cursor) this.cursor.resize();
		}
	}

	#onDOMContentLoaded = () => {
		if (!this.isIframe) {
			this.gatherElements();
			this.configureControllers();
			this.createComponents();
			this.checkScroll();

			if (this.isDesktop) {
				this.start();
			}

			ResizeController.add(this.#onWindowResize);
		}
	}

	#onDocumentMouseMove = (e) => {
		e.stopPropagation();

		this.mouse.x = event.touches ? event.touches[0].clientX : event.clientX;
		this.mouse.y = event.touches ? event.touches[0].clientY : event.clientY;

		if (this.cursor) {
			this.cursor.mouseMove(this.mouse);
		}
	}

	#onDocumentMouseDown = (e) => {
		e.stopPropagation();

		this.mouse.x = event.touches ? event.touches[0].clientX : event.clientX;
		this.mouse.y = event.touches ? event.touches[0].clientY : event.clientY;

		if (this.cursor) {
			this.cursor.mouseDown();
		}
	}

	#onDocumentMouseUp = (e) => {
		e.stopPropagation();

		this.mouse.x = event.touches ? event.touches[0].clientX : event.clientX;
		this.mouse.y = event.touches ? event.touches[0].clientY : event.clientY;

		if (this.cursor) {
			this.cursor.mouseUp();
		}
	}

	addEventListeners() {
		document.addEventListener('DOMContentLoaded', this.#onDOMContentLoaded);
		document.addEventListener('mousemove', this.#onDocumentMouseMove);
		document.addEventListener('mousedown', this.#onDocumentMouseDown);
		document.addEventListener('mouseup', this.#onDocumentMouseUp);
	}

	removeEventListeners() {
		document.removeEventListener('DOMContentLoaded', this.#onDOMContentLoaded);
		document.removeEventListener('mousemove', this.#onDocumentMouseMove);
		document.removeEventListener('mousedown', this.#onDocumentMouseDown);
		document.removeEventListener('mouseup', this.#onDocumentMouseUp);
	}

}

export default new App();
